// Implements: F-VER-006, F-UIX-021, F-UIX-007
/**
 * The banner over the canvas while it shows a version that is not the current one (UX-05, the
 * mockup's `.pv-banner`): it says which version is on screen and offers to use it as the current
 * one (the existing promote: a pointer move, no new version, F-VER-006) or to go back to the
 * current one. A refused promote is read out and the banner stays.
 */

import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { usePromoteVersion } from "../hooks/usePromoteVersion";
import { useVersionTree } from "../hooks/useVersionTree";
import styles from "./VersionPreviewBannerContainer.module.css";

export interface VersionPreviewBannerContainerProps {
  readonly mockupId: string;
  readonly versionId: string;
  /** Back to the current version (also once the shown one became the current one). */
  readonly onBack: () => void;
}

export function VersionPreviewBannerContainer({
  mockupId,
  versionId,
  onBack,
}: VersionPreviewBannerContainerProps): ReactElement | null {
  const { t } = useTranslation();
  const tree = useVersionTree(mockupId);
  const promote = usePromoteVersion();
  const { push } = useToast();
  const [pending, setPending] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  if (tree.status !== "success") return null;
  const node = tree.data.nodes.find((candidate) => candidate.id === versionId);
  if (node === undefined) return null;
  const label = t("editor.versions.viewing", { n: node.sequence_number });

  const use = async (): Promise<void> => {
    setPending(true);
    const refusal = await promote(mockupId, versionId);
    if (!alive.current) return;
    setPending(false);
    if (refusal === null) {
      push({ tone: "success", message: t("version.promoted") });
      onBack();
    } else {
      push({ tone: "danger", message: userMessage(refusal, t("version.promote.failed")) });
    }
  };

  return (
    <div className={styles.banner} role="status" aria-label={label}>
      <Icon name="eye" size="sm" />
      <span>{label}</span>
      <Button
        variant="ghost"
        size="sm"
        loading={pending}
        onClick={() => {
          void use();
        }}
      >
        {t("editor.versions.use")}
      </Button>
      <Button variant="secondary" size="sm" disabled={pending} onClick={onBack}>
        {t("editor.versions.backToCurrent")}
      </Button>
    </div>
  );
}
